import React, { useEffect, useRef, useState } from 'react';
import { useCurrency } from '../CurrencyContext';
import {
  usePendingTransactions,
  retryPendingUploads,
  removePendingUpload,
} from '../TransactionService';
import './PendingUploadsIndicator.css';

const useOnline = () => {
  const [online, setOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true,
  );
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);
  return online;
};

const fmtTime = (ts) => {
  if (!ts) return '';
  try {
    return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch (_) {
    return '';
  }
};

const PendingUploadsIndicator = () => {
  const items = usePendingTransactions();
  const online = useOnline();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const { currency } = useCurrency();
  const sym = currency.symbol;

  useEffect(() => {
    function handleClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const count = items.length;
  // Always rendered — sits permanently to the left of the profile avatar so
  // the user can open it any time to inspect (or confirm there's nothing) in
  // the upload queue.

  const hasFailure = items.some((i) => i.status === 'failed');
  const isUploading = items.some((i) => i.status === 'uploading');

  const stateClass = !online
    ? 'offline'
    : hasFailure
    ? 'failed'
    : isUploading
    ? 'uploading'
    : count > 0
    ? 'pending'
    : 'idle';

  return (
    <div className={`pq-wrap ${open ? 'open' : ''}`} ref={ref}>
      <button
        type="button"
        className={`pq-chip pq-chip-${stateClass}`}
        onClick={() => setOpen((v) => !v)}
        aria-label={`${count} pending uploads`}
        title={!online ? 'Offline — changes will sync when reconnected' : `${count} pending`}
      >
        <span className={`pq-icon pq-icon-${stateClass}`}>
          {!online ? (
            // Cloud-off icon
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="2" y1="2" x2="22" y2="22" />
              <path d="M5.16 5.16A4 4 0 0 0 8 13h11a4 4 0 0 0 1.16-7.84" />
              <path d="M16.94 16.94A4 4 0 0 1 14 18H8" />
            </svg>
          ) : isUploading ? (
            <span className="pq-spinner" />
          ) : hasFailure ? (
            // Warning triangle
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          ) : (
            // Cloud-up arrow
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 16.58A5 5 0 0 0 18 7h-1.26A8 8 0 1 0 4 15.25" />
              <polyline points="16 16 12 12 8 16" />
              <line x1="12" y1="12" x2="12" y2="21" />
            </svg>
          )}
        </span>
        {count > 0 && <span className="pq-count">{count}</span>}
      </button>

      {open && (
        <div className="pq-panel" role="dialog" aria-label="Pending uploads">
          <div className="pq-panel-head">
            <div className="pq-panel-title">
              {count === 0
                ? 'No transactions pending'
                : `${count} pending upload${count === 1 ? '' : 's'}`}
            </div>
            <div className={`pq-panel-status pq-status-${stateClass}`}>
              {!online
                ? 'Offline'
                : isUploading
                ? 'Uploading…'
                : hasFailure
                ? 'Will retry'
                : count > 0
                ? 'Queued'
                : 'Up to date'}
            </div>
          </div>

          {count === 0 ? (
            <div className="pq-empty">
              {online
                ? 'All transactions are uploaded. New ones will appear here while they sync.'
                : 'You are offline. Anything you add will be saved here and uploaded when you reconnect.'}
            </div>
          ) : (
            <ul className="pq-list">
              {items.map((item) => {
                const p = item.payload || {};
                const isCredit = p.transaction_type === 'credit';
                const amt = parseFloat(p.amount);
                return (
                  <li key={item.localId} className={`pq-item pq-item-${item.status}`}>
                    <div className="pq-item-row">
                      <div className="pq-item-icon">
                        {item.status === 'uploading' ? (
                          <span className="pq-spinner pq-spinner-sm" />
                        ) : item.status === 'failed' ? (
                          <span className="pq-dot pq-dot-failed" />
                        ) : (
                          <span className="pq-dot pq-dot-pending" />
                        )}
                      </div>
                      <div className="pq-item-main">
                        <div className="pq-item-line1">
                          <span className={`pq-item-amt ${isCredit ? 'credit' : 'debit'}`}>
                            {isCredit ? '+' : '-'}{sym}{Number.isFinite(amt) ? amt.toFixed(2) : '0.00'}
                          </span>
                          <span className="pq-item-cat">{p.category || 'uncategorised'}</span>
                        </div>
                        <div className="pq-item-line2">
                          {p.description ? (
                            <span className="pq-item-desc">{p.description}</span>
                          ) : (
                            <span className="pq-item-desc dim">No description</span>
                          )}
                          <span className="pq-item-meta">· queued {fmtTime(item.createdAt)}</span>
                        </div>
                        {item.status === 'failed' && item.error && (
                          <div className="pq-item-err">{item.error}</div>
                        )}
                      </div>
                      <button
                        type="button"
                        className="pq-item-discard"
                        title="Discard"
                        onClick={() => removePendingUpload(item.localId)}
                      >
                        ×
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          {count > 0 && (
            <div className="pq-panel-actions">
              <button
                type="button"
                className="pq-action"
                onClick={() => retryPendingUploads()}
                disabled={!online}
              >
                {online ? 'Retry now' : 'Waiting for connection'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PendingUploadsIndicator;
